import { Field, Form, Formik, FormikConfig } from "formik";

import { CaseAdmin } from "pwapi";

import useClinicFields from "shared/hooks/useClinicFields";
import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import TextInput from "components/customInputs/TextInput";

import FormikContext from "./FormikContext";
import { IntegrationValuesFormFields } from "./integrationFormUtils";
import { AdminFieldClinic, AdminFieldOrderingDoctor } from "../edit/fields";

type IntegrationsFormProps = {
  initialFields: IntegrationValuesFormFields;
  adminCase: CaseAdminDetail;
  onSubmit: FormikConfig<IntegrationValuesFormFields>["onSubmit"];
  setValues: Function;
  setEdited: Function;
  canUpdateCase: boolean;
};

const IntegrationsForm = ({
  initialFields,
  adminCase,
  onSubmit,
  setValues,
  setEdited,
  canUpdateCase,
}: IntegrationsFormProps) => {
  return (
    <Formik
      initialValues={initialFields}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {({ resetForm, dirty, values }) => {
        const emr = useClinicFields({
          clinic: values.clinic,
          skipMRN: true,
          flatIntegration: true,
        });
        return (
          <Form className="AdminPanelForm">
            <FormikContext setValues={setValues} setEdited={setEdited} />
            <div className="panelContainer">
              <div
                className="panelContainer__inputGroup"
                style={{ width: "100%" }}
              >
                <fieldset
                  className="panelContainer__section"
                  style={{ flex: 1 }}
                >
                  <legend className="panelContainer__section--title">
                    Patient Clinic
                  </legend>
                  <Field name="clinic">
                    {({ field, form }: any) => (
                      <AdminFieldClinic
                        {...field}
                        onChange={(newValue: any) => {
                          form.setFieldValue("clinic", newValue.object);
                          form.setFieldValue("ordering_doctor", undefined);
                        }}
                        disabled={!canUpdateCase}
                      />
                    )}
                  </Field>
                </fieldset>
                <fieldset
                  className="panelContainer__section"
                  style={{ flex: 1 }}
                >
                  <legend className="panelContainer__section--title">
                    Ordering Provider
                  </legend>
                  <Field name="ordering_doctor">
                    {({ field, form }: any) => (
                      <AdminFieldOrderingDoctor
                        {...field}
                        value={field.value}
                        patientClinicId={values?.clinic?.id}
                        onChange={(newValue: any) => {
                          form.setFieldValue(
                            "ordering_doctor",
                            newValue.object
                          );
                          setEdited(true);
                        }}
                        disabled={!canUpdateCase || !values?.clinic}
                      />
                    )}
                  </Field>
                </fieldset>
              </div>
              {adminCase.source === CaseAdmin.source.PW && (
                <fieldset className="panelContainer__section">
                  <legend className="panelContainer__section--title">
                    External IDs
                  </legend>
                  <div className="panelContainer__inputGroup">
                    {emr?.fields?.map((field) => {
                      const { name, ...rest } = field;
                      return (
                        <Field name={name} key={rest.id}>
                          {({ field }: any) => (
                            <TextInput
                              {...field}
                              {...rest}
                              label={
                                rest.required ? `${rest.label} *` : rest.label
                              }
                              disabled={!canUpdateCase}
                            />
                          )}
                        </Field>
                      );
                    })}
                    {(!emr || !emr?.fields) && (
                      <span>Clinic has no integrations</span>
                    )}
                  </div>
                </fieldset>
              )}

              <div className="form__actionContainer">
                <PathologyWatchButton
                  title="Discard changes to case"
                  color="red"
                  onClick={() => {
                    resetForm();
                  }}
                  disabled={!dirty}
                >
                  Discard Changes
                </PathologyWatchButton>
                <PathologyWatchButton
                  type="submit"
                  title="Save case"
                  disabled={!dirty || !canUpdateCase}
                  color="green"
                >
                  Save Changes
                </PathologyWatchButton>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default IntegrationsForm;
